import { parseCityId } from "@/utils/filters";
import {
  useCityAppConfiguration,
  useOrgAppConfiguration,
} from "@/v2/queries/appConfiguration";
import { useFleetByCity } from "@/v2/queries/reservations";
import { useSavedCity } from "@/v2/stores";
import {
  type MeasurementSystem,
  resolveMeasurementSystem,
} from "@/v2/utils/measurement";
import { useEffectiveWhitelabelId } from "./useEffectiveWhitelabelId";
import { useOrganizationId } from "./useOrganizationId";

/**
 * Effective display units: the selected city's override, else the organization default,
 * else metric.
 *
 * The organization is reached by two routes because either half of the navbar selection can
 * be empty on its own: a city resolves it through its fleet, and a whitelabel resolves it
 * directly. Relying on only one of them leaves the other selection rendering metric for an
 * organization configured imperial.
 *
 * Metric is what callers get until the configuration queries resolve, so an imperial
 * organization does briefly render metric on first paint.
 */
export function useMeasurementSystem(): MeasurementSystem {
  const cityId = parseCityId(useSavedCity());
  const { data: fleet } = useFleetByCity(cityId);
  const whitelabelId = useEffectiveWhitelabelId();
  const organizationIdFromWhitelabel = useOrganizationId(whitelabelId);
  const organizationId = fleet?.organization_id ?? organizationIdFromWhitelabel;
  const { data: orgSettings } = useOrgAppConfiguration(organizationId);
  const { data: citySettings } = useCityAppConfiguration(cityId);

  return resolveMeasurementSystem(
    citySettings?.measurement_system,
    orgSettings?.measurement_system
  );
}
